import { AppProps } from 'next/app';
import { Hydrate, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import {
  CollectionLayout,
  DefaultLayout,
  ErrorFallback,
  DetailPageLayout,
  RootContainer,
} from '@/components/common';
import { ThemeProvider } from 'styled-components';
import GlobalStyle from '@/styles/GlobalStyles';
import { theme } from '@/styles/theme';
import 'swiper/swiper.min.css';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import * as gtag from '@/lib/gtag';
import { MEDIA_SEQ } from '@/const';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

export default function App({ Component, pageProps }: AppProps) {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 10,
        cacheTime: 1000 * 60 * 10,
      },
    },
  });
  const router = useRouter();
  const pathname = router.pathname;
  const media = router.query.media as string;
  const mediaSeq = media && MEDIA_SEQ[media.toUpperCase()];
  const showNavbar =
    mediaSeq && [MEDIA_SEQ.LGU, MEDIA_SEQ.L_POINT, MEDIA_SEQ.SKT, MEDIA_SEQ.KT].includes(mediaSeq);

  let Layout = DefaultLayout;
  if (media && showNavbar) {
    Layout = CollectionLayout;
    if (pathname?.includes('promotion') || pathname?.includes('template')) {
      Layout = DetailPageLayout;
    }
  }

  useEffect(() => {
    const handleRouteChange = (url: any) => {
      gtag.pageview(url);
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    router.events.on('hashChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
      router.events.off('hashChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <Hydrate state={pageProps.dehydratedState}>
          <ThemeProvider theme={theme}>
            <GlobalStyle />
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <RootContainer>
                <Layout>
                  <Component {...pageProps} />
                </Layout>
              </RootContainer>
            </ErrorBoundary>
          </ThemeProvider>
        </Hydrate>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </>
  );
}
